import { SidebarHeader } from './SidebarHeader';
import { SidebarNav } from './SidebarNav';
import { SidebarFooter } from './SidebarFooter';
import { Menu, X } from 'lucide-react';
import { useUIStore } from '@/features/dashboard/stores/ui.store';
import { useEffect } from 'react';

interface SidebarProps {
  onLogout: () => void;
  onHelpClick: () => void;
}

export const Sidebar = ({ onLogout, onHelpClick }: SidebarProps) => {
  const { isMenuOpen, toggleMenu, setMenuOpen } = useUIStore();

  // Initialize menu state based on screen size on mount
  useEffect(() => {
    const isLargeScreen = window.innerWidth >= 1024;
    setMenuOpen(isLargeScreen);
  }, [setMenuOpen]);

  // Handle window resize events
  useEffect(() => {
    const handleResize = () => {
      const isLargeScreen = window.innerWidth >= 1024;
      if (!isLargeScreen && isMenuOpen) {
        // Close menu on small screens
        setMenuOpen(false);
      } else if (isLargeScreen && !isMenuOpen) {
        // Open menu on large screens
        setMenuOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen, setMenuOpen]);

  return (
    <>
      {/* Main Sidebar Content */}
      <div
        className={`
        fixed top-0 left-0
        flex flex-col h-screen bg-gray-800
        transition-all duration-300 ease-in-out
        ${isMenuOpen ? 'w-64 translate-x-0' : 'w-0 -translate-x-full lg:w-0 lg:-translate-x-full'}
        overflow-hidden z-50
      `}
      >
        <div
          className={`
          ${isMenuOpen ? 'opacity-100' : 'opacity-0'}
          transition-opacity duration-300
          ${isMenuOpen ? 'min-w-[256px]' : 'min-w-0'}
        `}
        >
          <SidebarHeader />
          <SidebarNav />
          <SidebarFooter onLogout={onLogout} onHelpClick={onHelpClick} />
        </div>
      </div>

      {/* Toggle Button - Top Left */}
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={toggleMenu}
          className={`h-10 w-10 rounded-md flex items-center justify-center transition-colors ${
            isMenuOpen
              ? 'bg-gray-800 text-white hover:bg-gray-700 border border-transparent shadow-lg'
              : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 shadow-sm'
          }`}
          aria-label="Toggle sidebar"
        >
          {isMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>
    </>
  );
};
